import { z } from "zod";
import type { AnalyticsZone } from "./access.ts";
import { PULSE_MOVES, moveTone } from "./analytics-metrics.ts";
import type { Move, Outlook } from "./analytics-metrics.ts";
import { DISCORD_SNOWFLAKE } from "./discord.ts";
import { JOIN_SOURCES } from "./join-sources.ts";
import { modulePath } from "./module.ts";

export const PULSE_READINGS = [
  "active_members",
  "talk_concentration",
  "quiet_channels",
  "newcomer_reception",
  "activation",
  "early_churn",
  "unanswered",
  "net_new",
] as const;

export type PulseReading = (typeof PULSE_READINGS)[number];

export const PULSE_BETTER = {
  active_members: "up",
  talk_concentration: "down",
  quiet_channels: "down",
  newcomer_reception: "up",
  activation: "up",
  early_churn: "down",
  unanswered: "down",
  net_new: "up",
} as const satisfies Readonly<Record<PulseReading, Move>>;

export const PULSE_READING_VIEWS = {
  active_members: "engagement",
  talk_concentration: "engagement",
  quiet_channels: "engagement/channels",
  newcomer_reception: "growth",
  activation: "growth",
  early_churn: "growth",
  unanswered: "engagement",
  net_new: "growth",
} as const satisfies Readonly<Record<PulseReading, string>>;

export const FINDING_VIEWS = {
  engagement: "engagement",
  channel: "engagement/channels/detail",
  growth: "growth",
  inviters: "growth/invites/inviters",
  inviteLinks: "growth/invites/links",
  levelingCurve: "leveling/progression/earning",
} as const;

export const PULSE_UNITS = ["share", "count"] as const;

export const FINDING_SEVERITIES = ["attention", "notable", "info"] as const;

export type FindingSeverity = (typeof FINDING_SEVERITIES)[number];

const FINDING_DIMENSIONS = ["channel", "role", "tenure", "hour", "join_source"] as const;

export type FindingDimension = (typeof FINDING_DIMENSIONS)[number];

export const TENURE_BANDS = ["first_week", "first_month", "first_quarter", "settled"] as const;

export const NEWCOMER_STEPS = ["spoke", "answered", "returned"] as const;

export type NewcomerStep = (typeof NEWCOMER_STEPS)[number];

export const FARMING_PATTERNS = ["solo_voice", "steady_cadence", "short_messages"] as const;

export type FarmingPattern = (typeof FARMING_PATTERNS)[number];

const NEWCOMER_BEHAVIOURS = ["answered", "first_channel", "voice", "role", "partners"] as const;

export type NewcomerBehaviour = (typeof NEWCOMER_BEHAVIOURS)[number];

export const COHORT_MEMBERS_LISTED = 25;

const snowflake = z.string().regex(DISCORD_SNOWFLAKE, "must be a Discord id");

const day = z.iso.date();

const move = z.enum(PULSE_MOVES);

const cohortSchema = z.discriminatedUnion("dimension", [
  z.object({ dimension: z.enum(["channel", "role"]), key: snowflake }),
  z.object({ dimension: z.literal("tenure"), key: z.enum(TENURE_BANDS) }),
  z.object({ dimension: z.literal("join_source"), key: z.enum(JOIN_SOURCES) }),
  z.object({ dimension: z.literal("partner_left"), key: z.literal("left") }),
]);

export type DriftingCohort = z.infer<typeof cohortSchema>;

const findingDriverSchema = z.object({
  dimension: z.enum(FINDING_DIMENSIONS),
  key: z.string(),
  before: z.number(),
  after: z.number(),
  contribution: z.number(),
});

export type FindingDriver = z.infer<typeof findingDriverSchema>;

export const xpMultiplierActionSchema = z.object({
  kind: z.literal("xp_multiplier"),
  channels: z.array(snowflake).min(1),
  factor: z.number().positive(),
  days: z.number().int().min(1).max(31),
});

export const findingActionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("see_view"), view: z.string() }),
  z.object({ kind: z.literal("reach_out"), userId: snowflake }),
  xpMultiplierActionSchema,
  z.object({ kind: z.literal("review_reward"), level: z.number().int().min(1) }),
]);

export type FindingAction = z.infer<typeof findingActionSchema>;

const base = {
  id: z.string(),
  severity: z.enum(FINDING_SEVERITIES),
  score: z.number(),
  window: z.object({ since: day, until: day }),
  drivers: z.array(findingDriverSchema),
  actions: z.array(findingActionSchema),
};

const kind = <K extends string, S extends z.ZodRawShape>(name: K, facts: S) =>
  z.object({ ...base, kind: z.literal(name), ...facts });

const memberIds = z.array(snowflake).max(COHORT_MEMBERS_LISTED);

const newcomerPathFacts = {
  took: z.number().int(),
  tookRate: z.number(),
  skipped: z.number().int(),
  skippedRate: z.number(),
  lift: z.number(),
  low: z.number(),
  high: z.number(),
};

const NAMED_BEHAVIOURS = ["first_channel", "role"] as const;

const behaviour = z.enum(NEWCOMER_BEHAVIOURS);

const namedBehaviour = { behaviour: behaviour.extract(NAMED_BEHAVIOURS), key: snowflake };

const plainBehaviour = { behaviour: behaviour.exclude(NAMED_BEHAVIOURS), key: z.null() };

export type NewcomerPathSubject =
  | z.infer<z.ZodObject<typeof namedBehaviour>>
  | z.infer<z.ZodObject<typeof plainBehaviour>>;

const newcomerPathSchema = z.discriminatedUnion("behaviour", [
  kind("newcomer_path", { ...namedBehaviour, ...newcomerPathFacts }),
  kind("newcomer_path", { ...plainBehaviour, ...newcomerPathFacts }),
]);

export const findingSchema = z.discriminatedUnion("kind", [
  kind("reading_moved", {
    reading: z.enum(PULSE_READINGS),
    unit: z.enum(PULSE_UNITS),
    current: z.number(),
    median: z.number(),
    low: z.number(),
    high: z.number(),
    direction: move,
  }),
  kind("regular_drifting", {
    userId: snowflake,
    activeWeeks: z.number().int(),
    weeksJudged: z.number().int(),
    usualPerWeek: z.number(),
    quietDays: z.number().int(),
    lastSeen: day,
    lastChannelId: snowflake.nullable(),
    partnerLeftId: snowflake.nullable(),
  }),
  kind("rising_member", {
    userId: snowflake,
    joinedOn: day,
    messages: z.number().int(),
    partners: z.number().int(),
    repliesReceived: z.number().int(),
  }),
  kind("newcomer_leak", {
    step: z.enum(NEWCOMER_STEPS),
    cohort: z.number().int(),
    took: z.number().int(),
    rate: z.number(),
    usual: z.number(),
  }),
  kind("invite_quality", {
    inviterId: snowflake.nullable(),
    inviteCode: z.string().nullable(),
    joins: z.number().int(),
    retained: z.number().int(),
    rate: z.number(),
    serverRate: z.number(),
  }),
  kind("join_burst", {
    at: z.iso.datetime(),
    joins: z.number().int(),
    usualHigh: z.number(),
    youngShare: z.number(),
  }),
  kind("channel_fading", {
    channelId: snowflake,
    current: z.number(),
    usual: z.number(),
    weeksDown: z.number().int(),
  }),
  kind("reward_effect", {
    level: z.number().int(),
    roleId: snowflake,
    effect: z.number(),
    low: z.number(),
    high: z.number(),
    above: z.number().int(),
    below: z.number().int(),
  }),
  kind("level_stall", {
    level: z.number().int(),
    members: z.number().int(),
    medianDays: z.number(),
    leftShare: z.number(),
  }),
  kind("xp_farming", {
    userId: snowflake,
    pattern: z.enum(FARMING_PATTERNS),
    share: z.number(),
    xp: z.number(),
  }),
  kind("cohort_drifting", {
    cohort: cohortSchema,
    regulars: z.number().int(),
    drifting: z.number().int(),
    rate: z.number(),
    low: z.number(),
    high: z.number(),
    restRate: z.number(),
    memberIds,
  }),
  kind("join_cohort_activation", {
    joiners: z.number().int(),
    regulars: z.number().int(),
    rate: z.number(),
    usual: z.number(),
    priorJoiners: z.number().int(),
    direction: move,
  }),
  newcomerPathSchema,
  kind("farming_share", {
    pattern: z.enum(FARMING_PATTERNS),
    farmers: z.number().int(),
    earners: z.number().int(),
    xp: z.number(),
    share: z.number(),
    channels: z.array(z.object({ channelId: snowflake, share: z.number() })),
    memberIds,
  }),
]);

export type Finding = z.infer<typeof findingSchema>;

export type FindingKind = Finding["kind"];

export const FINDING_KINDS: readonly FindingKind[] = [
  ...new Set(
    findingSchema.options.flatMap((option) =>
      "options" in option
        ? option.options.map((variant) => variant.shape.kind.value)
        : [option.shape.kind.value],
    ),
  ),
];

export const FINDING_ZONES: Readonly<Record<FindingKind, AnalyticsZone>> = {
  reading_moved: "engagement",
  regular_drifting: "profiles",
  rising_member: "profiles",
  newcomer_leak: "growth",
  invite_quality: "growth",
  join_burst: "growth",
  channel_fading: "engagement",
  reward_effect: "engagement",
  level_stall: "engagement",
  xp_farming: "profiles",
  cohort_drifting: "engagement",
  join_cohort_activation: "growth",
  newcomer_path: "growth",
  farming_share: "engagement",
};

export const findingZones = (finding: Finding): readonly AnalyticsZone[] => {
  const zone = FINDING_ZONES[finding.kind];
  const namesMember = finding.kind === "invite_quality" && finding.inviterId !== null;
  return namesMember && zone !== "profiles" ? [zone, "profiles"] : [zone];
};

type FindingOf<K extends FindingKind> = Extract<Finding, { kind: K }>;

export const rewardTrend = ({
  low,
  high,
}: Pick<FindingOf<"reward_effect">, "low" | "high">): "lifts" | "dampens" | "unclear" =>
  low > 1 ? "lifts" : high < 1 ? "dampens" : "unclear";

export const inviteDirection = ({
  rate,
  serverRate,
}: Pick<FindingOf<"invite_quality">, "rate" | "serverRate">): "better" | "worse" =>
  rate >= serverRate ? "better" : "worse";

export const pathDirection = ({
  lift,
}: Pick<FindingOf<"newcomer_path">, "lift">): "more" | "less" => (lift >= 1 ? "more" : "less");

export const withoutMembers = (finding: Finding): Finding =>
  "memberIds" in finding ? { ...finding, memberIds: [] } : finding;

export const BRIEFING_ITEM_STATES = ["acknowledged", "done", "dismissed"] as const;

export type BriefingItemState = (typeof BRIEFING_ITEM_STATES)[number];

export const BRIEFING_SENSITIVITIES = ["low", "medium", "high"] as const;

export type BriefingSensitivity = (typeof BRIEFING_SENSITIVITIES)[number];

export const SENSITIVITY_SEVERITIES: Readonly<
  Record<BriefingSensitivity, readonly FindingSeverity[]>
> = {
  low: ["attention"],
  medium: ["attention", "notable"],
  high: FINDING_SEVERITIES,
};

export const BRIEFING_PRESSES = ["seen", "done", "dismiss", "assign", "snooze"] as const;

export type BriefingPress = (typeof BRIEFING_PRESSES)[number];

export const BRIEFING_ASKS = [...BRIEFING_PRESSES, "view"] as const;

export type BriefingAsk = (typeof BRIEFING_ASKS)[number];

export const PRESSED_STATE = {
  seen: "acknowledged",
  done: "done",
  dismiss: "dismissed",
} as const satisfies Partial<Record<BriefingPress, BriefingItemState>>;

const BRIEFING_CUSTOM_ID = new RegExp(
  `^brief:(${BRIEFING_PRESSES.join("|")}):([0-9a-f]{24}):(\\d{17,20})$`,
);

export const briefingCustomId = (
  press: BriefingPress,
  findingId: string,
  guildId: string,
): string => `brief:${press}:${findingId}:${guildId}`;

export const parseBriefingCustomId = (
  customId: string,
): {
  readonly press: BriefingPress;
  readonly findingId: string;
  readonly guildId: string;
} | null => {
  const [, press, findingId, guildId] = BRIEFING_CUSTOM_ID.exec(customId) ?? [];
  if (press === undefined || findingId === undefined || guildId === undefined) return null;
  return { press: press as BriefingPress, findingId, guildId };
};

const BRIEFING_PICK_ID = /^brief:pick:(\d{17,20})$/;

export const briefingPickCustomId = (guildId: string): string => `brief:pick:${guildId}`;

export const parseBriefingPickCustomId = (
  customId: string,
): { readonly guildId: string } | null => {
  const [, guildId] = BRIEFING_PICK_ID.exec(customId) ?? [];
  return guildId === undefined ? null : { guildId };
};

export const FINDINGS_ANCHOR = "findings";

export const findingsPath = (tenantId: string): string =>
  `${modulePath(tenantId, "intelligence")}#${FINDINGS_ANCHOR}`;

export const BRIEFING_ANCHOR = "briefing";

export const briefingPath = (tenantId: string): string =>
  modulePath(tenantId, "intelligence", "briefing");

export const FIGURE_TONES = ["critical", "warning", "good", "neutral"] as const;

export type FigureTone = (typeof FIGURE_TONES)[number];

export type FigureUnit = "share" | "count" | "change";

export interface FindingFigure {
  readonly value: number;
  readonly unit: FigureUnit;
  readonly usual: { readonly low: number; readonly high: number } | null;
}

const against = (low: number, high: number = low) => ({ low, high });

type Facts<T> = T extends unknown
  ? { readonly [K in keyof T as T[K] extends readonly unknown[] ? never : K]: T[K] }
  : never;

export type FindingFacts = Facts<Finding>;

export const findingOutlook = (finding: FindingFacts): Outlook => {
  switch (finding.kind) {
    case "reading_moved":
      return moveTone(finding.direction, PULSE_BETTER[finding.reading]);
    case "join_cohort_activation":
      return moveTone(finding.direction, "up");
    case "invite_quality":
      return inviteDirection(finding) === "better" ? "good" : "bad";
    case "newcomer_path":
      return pathDirection(finding) === "more" ? "good" : "bad";
    case "reward_effect": {
      const trend = rewardTrend(finding);
      return trend === "lifts" ? "good" : trend === "dampens" ? "bad" : "neutral";
    }
    case "join_burst":
      return finding.severity === "attention" ? "bad" : "neutral";
    case "rising_member":
      return "good";
    case "regular_drifting":
    case "newcomer_leak":
    case "channel_fading":
    case "level_stall":
    case "xp_farming":
    case "cohort_drifting":
    case "farming_share":
      return "bad";
  }
};

export const findingTone = (finding: FindingFacts): FigureTone => {
  const outlook = findingOutlook(finding);
  if (outlook !== "bad") return outlook;
  return finding.severity === "attention" ? "critical" : "warning";
};

export const findingFigure = (finding: FindingFacts): FindingFigure => {
  switch (finding.kind) {
    case "reading_moved":
      return {
        value: finding.current,
        unit: finding.unit,
        usual: against(finding.low, finding.high),
      };
    case "regular_drifting":
      return { value: finding.quietDays, unit: "count", usual: null };
    case "rising_member":
      return { value: finding.messages, unit: "count", usual: null };
    case "newcomer_leak":
      return { value: finding.rate, unit: "share", usual: against(finding.usual) };
    case "invite_quality":
      return { value: finding.rate, unit: "share", usual: against(finding.serverRate) };
    case "join_burst":
      return { value: finding.joins, unit: "count", usual: against(0, finding.usualHigh) };
    case "channel_fading":
      return { value: finding.current, unit: "count", usual: against(finding.usual) };
    case "reward_effect":
      return { value: finding.effect - 1, unit: "change", usual: null };
    case "level_stall":
      return { value: finding.members, unit: "count", usual: null };
    case "xp_farming":
      return { value: finding.share, unit: "share", usual: null };
    case "cohort_drifting":
      return { value: finding.rate, unit: "share", usual: against(finding.restRate) };
    case "join_cohort_activation":
      return { value: finding.rate, unit: "share", usual: against(finding.usual) };
    case "newcomer_path":
      return { value: finding.tookRate, unit: "share", usual: against(finding.skippedRate) };
    case "farming_share":
      return { value: finding.share, unit: "share", usual: null };
  }
};

export interface FigureStrip {
  readonly from: number;
  readonly to: number;
  readonly at: number;
}

const STRIP_HEADROOM = 1.25;

export const figureStrip = ({ value, usual }: FindingFigure): FigureStrip | null => {
  if (usual === null) return null;
  const top = Math.max(value, usual.high, 0) * STRIP_HEADROOM;
  if (top <= 0) return null;
  const place = (point: number): number => Math.min(1, Math.max(0, point / top));
  return { from: place(usual.low), to: place(usual.high), at: place(value) };
};
